/**
 * The Object Editor's document: the draft spec, the selection and snapshot
 * undo history. React reads it through `useSyncExternalStore`
 * (useEditorState); tools and panels write through `apply` / transactions.
 *
 * Drags are TRANSACTIONS: `begin()` remembers the spec, every move applies
 * transiently (no history entry), `commit()` pushes the pre-drag spec as ONE
 * undo step (or nothing, when the drag changed nothing).
 */

import type { ObjectDefinitionSpec } from 'shared-types';

export type EditorSelection = { kind: 'none' } | { kind: 'shapes'; ids: readonly string[] } | { kind: 'group'; id: string } | { kind: 'param'; key: string };

export const NO_SELECTION: EditorSelection = { kind: 'none' };

export interface EditorState {
  readonly spec: ObjectDefinitionSpec;
  readonly selection: EditorSelection;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  /** Bumped on every spec change (autosave watches it); not on selection changes. */
  readonly revision: number;
}

const HISTORY_LIMIT = 200;

export class EditorStore {
  private state: EditorState;
  private readonly listeners = new Set<() => void>();
  private undoStack: ObjectDefinitionSpec[] = [];
  private redoStack: ObjectDefinitionSpec[] = [];
  private transactionBase: ObjectDefinitionSpec | null = null;

  constructor(spec: ObjectDefinitionSpec) {
    this.state = { spec, selection: NO_SELECTION, canUndo: false, canRedo: false, revision: 0 };
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getState = (): EditorState => this.state;

  private set(patch: Partial<EditorState>): void {
    this.state = { ...this.state, ...patch, canUndo: this.undoStack.length > 0, canRedo: this.redoStack.length > 0 };
    for (const listener of this.listeners) listener();
  }

  /**
   * Apply an edit. Inside a transaction it is transient (history is written
   * at commit); otherwise it is one undo step. Returning the same spec object
   * is a no-op.
   */
  apply(edit: (spec: ObjectDefinitionSpec) => ObjectDefinitionSpec, selection?: EditorSelection): void {
    const next = edit(this.state.spec);
    if (next === this.state.spec) {
      if (selection) this.select(selection);
      return;
    }
    if (!this.transactionBase) this.pushHistory(this.state.spec);
    this.set({ spec: next, revision: this.state.revision + 1, ...(selection ? { selection } : {}) });
  }

  begin(): void {
    this.transactionBase ??= this.state.spec;
  }

  commit(): void {
    const base = this.transactionBase;
    this.transactionBase = null;
    if (base && base !== this.state.spec) {
      this.pushHistory(base);
      this.set({});
    }
  }

  /** Abort a transaction: the spec goes back to where the drag started. */
  rollback(): void {
    const base = this.transactionBase;
    this.transactionBase = null;
    if (base && base !== this.state.spec) this.set({ spec: base, revision: this.state.revision + 1 });
  }

  private pushHistory(spec: ObjectDefinitionSpec): void {
    this.undoStack.push(spec);
    if (this.undoStack.length > HISTORY_LIMIT) this.undoStack.shift();
    this.redoStack = [];
  }

  undo(): void {
    const previous = this.undoStack.pop();
    if (!previous) return;
    this.redoStack.push(this.state.spec);
    this.set({ spec: previous, selection: this.pruneSelection(previous), revision: this.state.revision + 1 });
  }

  redo(): void {
    const next = this.redoStack.pop();
    if (!next) return;
    this.undoStack.push(this.state.spec);
    this.set({ spec: next, selection: this.pruneSelection(next), revision: this.state.revision + 1 });
  }

  select(selection: EditorSelection): void {
    if (selection === this.state.selection) return;
    this.set({ selection });
  }

  /**
   * Replace the whole document (discard draft, load from server). Clears the
   * history: the old undo steps belong to a different starting point.
   */
  reset(spec: ObjectDefinitionSpec): void {
    this.undoStack = [];
    this.redoStack = [];
    this.transactionBase = null;
    this.set({ spec, selection: this.pruneSelection(spec), revision: this.state.revision + 1 });
  }

  /** Drop selected things that no longer exist in `spec`. */
  private pruneSelection(spec: ObjectDefinitionSpec): EditorSelection {
    const sel = this.state.selection;
    switch (sel.kind) {
      case 'shapes': {
        const ids = sel.ids.filter((id) => spec.shapes.some((s) => s.id === id));
        return ids.length > 0 ? { kind: 'shapes', ids } : NO_SELECTION;
      }
      case 'group':
        return spec.groups.some((g) => g.id === sel.id) ? sel : NO_SELECTION;
      case 'param':
        return spec.params.some((p) => p.key === sel.key) ? sel : NO_SELECTION;
      case 'none':
        return sel;
    }
  }
}
